// Loan record foundations — right-side edit drawer for one Parties-tab row.
//
// FIND-12 discipline: EVERY editable field hydrates from the existing
// assignment when the drawer opens, and Save always sends the full current
// form state — so "open → save unchanged" round-trips the same values and a
// blank optional field can never silently wipe a relationship the form
// didn't show. (The broker-affiliation modal bug was exactly a date pair
// that rendered un-hydrated and wiped on save.)

import { useEffect, useState } from "react";
import { Button, FormField, Input, Textarea } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useUpdateLoanParty } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { ResourceDrawer } from "../common/ResourceDrawer";
import type { LoanPartyAssignmentDto } from "@/api/portfolioAdmin/types";

interface EditPartyDrawerProps {
  loanId: string;
  party: LoanPartyAssignmentDto;
  open: boolean;
  onClose: () => void;
}

/** ISO timestamp → yyyy-MM-dd for a date input ("" when unset). */
function toDateInput(iso: string | null): string {
  return iso ? iso.slice(0, 10) : "";
}

export function EditPartyDrawer({ loanId, party, open, onClose }: EditPartyDrawerProps) {
  const toast = useToast();
  const update = useUpdateLoanParty(loanId);

  const [role, setRole] = useState(party.roleOnLoan);
  const [emailOverride, setEmailOverride] = useState(party.emailOverride ?? "");
  const [phoneOverride, setPhoneOverride] = useState(party.phoneOverride ?? "");
  const [addressOverride, setAddressOverride] = useState(party.addressOverride ?? "");
  const [notes, setNotes] = useState(party.notes ?? "");
  const [effectiveFrom, setEffectiveFrom] = useState(toDateInput(party.effectiveFrom));
  const [effectiveTo, setEffectiveTo] = useState(toDateInput(party.effectiveTo));
  const [isKeyPerson, setIsKeyPerson] = useState(party.isKeyPerson);
  const [isPrimaryContact, setIsPrimaryContact] = useState(party.isPrimaryContact);

  // Hydrate from the row every time the drawer opens (never a blank form).
  useEffect(() => {
    if (!open) return;
    setRole(party.roleOnLoan);
    setEmailOverride(party.emailOverride ?? "");
    setPhoneOverride(party.phoneOverride ?? "");
    setAddressOverride(party.addressOverride ?? "");
    setNotes(party.notes ?? "");
    setEffectiveFrom(toDateInput(party.effectiveFrom));
    setEffectiveTo(toDateInput(party.effectiveTo));
    setIsKeyPerson(party.isKeyPerson);
    setIsPrimaryContact(party.isPrimaryContact);
  }, [open, party]);

  const onSave = async () => {
    if (!role.trim()) {
      toast.error("Role on loan is required.");
      return;
    }
    try {
      await update.mutateAsync({
        partyAssignmentId: party.id,
        body: {
          roleOnLoan: role.trim(),
          emailOverride,
          phoneOverride,
          addressOverride,
          notes,
          effectiveFrom,
          effectiveTo,
          isKeyPerson,
          isPrimaryContact,
        },
      });
      toast.success("Party updated.");
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      title={party.counterpartyName ?? `${party.counterpartyType} · ${party.counterpartyId.slice(0, 8)}…`}
      subtitle={`${party.counterpartyType} · ${party.roleOnLoan}`}
      testId="edit-party-drawer"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSave()}
            disabled={update.isPending || !role.trim()}
            data-testid="edit-party-save"
          >
            {update.isPending ? "Saving…" : "Save changes"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <FormField label="Role on loan" required>
          <Input
            value={role}
            onChange={(e) => setRole(e.target.value)}
            data-testid="edit-party-role"
          />
        </FormField>

        <FormField
          label="Email override"
          helperText="Loan-scoped — leaves the counterparty record untouched. Blank uses the canonical email."
        >
          <Input
            type="email"
            value={emailOverride}
            onChange={(e) => setEmailOverride(e.target.value)}
            data-testid="edit-party-email-override"
          />
        </FormField>

        <FormField label="Phone override" helperText="Blank uses the canonical phone.">
          <Input
            value={phoneOverride}
            onChange={(e) => setPhoneOverride(e.target.value)}
          />
        </FormField>

        <FormField label="Address override">
          <Textarea
            value={addressOverride}
            onChange={(e) => setAddressOverride(e.target.value)}
            rows={2}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-space-3">
          <FormField label="Effective from">
            <Input
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              data-testid="edit-party-effective-from"
            />
          </FormField>
          <FormField label="Effective to" helperText="Set when the party leaves the loan.">
            <Input
              type="date"
              value={effectiveTo}
              onChange={(e) => setEffectiveTo(e.target.value)}
              data-testid="edit-party-effective-to"
            />
          </FormField>
        </div>

        <label className="flex items-center gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            checked={isKeyPerson}
            onChange={(e) => setIsKeyPerson(e.target.checked)}
            data-testid="edit-party-key-person"
          />
          Key person (ties to Key-Man insurance)
        </label>

        <label className="flex items-center gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            checked={isPrimaryContact}
            onChange={(e) => setIsPrimaryContact(e.target.checked)}
            data-testid="edit-party-primary-contact"
          />
          Primary contact (one per loan — setting this clears the prior holder)
        </label>

        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            data-testid="edit-party-notes"
          />
        </FormField>
      </div>
    </ResourceDrawer>
  );
}
